import { Lock } from "lucide-react";
import { useMemo, type ReactNode } from "react";

import { AccessEffectBadge } from "@/modules/dashboard/settings/components/access/AccessEffectBadge";
import { AccessScopeBadge } from "@/modules/dashboard/settings/components/access/AccessScopeBadge";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { useTranslations } from "@/shared/lang/useTranslations";

import type { PolicyRow } from "../hooks/usePoliciesCatalog";
import { PolicyStatementsCell } from "./PolicyStatementsCell";

export interface PoliciesTableProps {
  rows: readonly PolicyRow[];
  loading: boolean;
  empty: ReactNode;
  footer?: ReactNode;
  /** The roles catalog could not be read, so no policy's usage is known. See the "Usada por" column. */
  usageUnknown: boolean;
  onOpen: (row: PolicyRow) => void;
}

/**
 * The catalog at `lg` and up; below that the same rows render as cards.
 *
 * No sorting: `GET /internal/policies` fixes the order and accepts no `sort`, so no header is clickable — a
 * sort control that does not sort is worse than none.
 *
 * The whole row opens the editor, in the same read-only state Settings gives a protected policy, so there is
 * no actions column to hold a single button.
 */
export function PoliciesTable({ rows, loading, empty, footer, usageUnknown, onOpen }: PoliciesTableProps) {
  const { t } = useTranslations("settings");

  const columns = useMemo<ColocaDataTableColumn<PolicyRow>[]>(
    () => [
      {
        id: "policy",
        header: t("access_policies_col_policy"),
        headerClassName: "min-w-[240px]",
        cell: ({ policy }) => (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[13px] font-semibold text-foreground">{policy.name}</span>
              {policy.is_protected && (
                <Lock
                  size={11}
                  className="shrink-0 text-muted-foreground/70"
                  aria-label={t("access_policies_protected_hint")}
                />
              )}
            </div>
            {policy.description && (
              <div className="truncate text-[11.5px] text-muted-foreground">{policy.description}</div>
            )}
          </div>
        ),
      },
      {
        id: "effect",
        header: t("access_policies_col_effect"),
        headerClassName: "w-[110px]",
        cell: ({ policy }) => <AccessEffectBadge effect={policy.effect} />,
      },
      {
        id: "scope",
        header: t("access_page_col_scope"),
        headerClassName: "w-[110px]",
        cell: ({ policy }) => <AccessScopeBadge organizationId={policy.organization_id} />,
      },
      {
        id: "permissions",
        header: t("access_policies_col_permissions"),
        headerClassName: "min-w-[260px]",
        cell: (row) => (
          <PolicyStatementsCell
            statements={row.policy.statements}
            effect={row.policy.effect}
            resourceCount={row.resourceCount}
          />
        ),
      },
      {
        id: "used_by",
        header: t("access_policies_col_used_by"),
        headerClassName: "w-[150px]",
        // Three distinct answers, and collapsing any two of them misleads. `?`: the roles catalog could not
        // be read, so nothing is known. The unused badge: read, and no role links it — a finding, since that
        // is also the only policy the backend will let you delete (`DELETE` answers 409 otherwise). A count:
        // read, and this many link it.
        cell: ({ usedBy }) =>
          usageUnknown ? (
            <span
              className="font-mono text-[12px] font-semibold text-muted-foreground/70"
              title={t("access_policies_usage_unknown_title")}
            >
              ?
            </span>
          ) : usedBy === 0 ? (
            <ColocaBadge variant="warning">{t("access_policies_unused_badge")}</ColocaBadge>
          ) : (
            <span className="font-mono text-[11px] text-muted-foreground">
              {usedBy === 1
                ? t("access_policy_used_by_singular")
                : t("access_policy_used_by_plural", { count: usedBy })}
            </span>
          ),
      },
    ],
    [t, usageUnknown],
  );

  return (
    <ColocaDataTable<PolicyRow>
      columns={columns}
      rows={rows as PolicyRow[]}
      rowKey={(row) => row.policy.id}
      onRowClick={onOpen}
      loading={loading}
      skeletonRows={8}
      empty={empty}
      footer={footer}
      // The page already wraps this in a `ColocaSurface`; a second frame would double the hairline and notch
      // every corner. Sum of the declared column widths — below it the table scrolls.
      frame="none"
      minWidth={870}
      // Dim, never tint. The deny rows in particular are NOT coloured: `AccessEffectBadge` already carries
      // that distinction, and tinting them too would turn the table into a colour chart the eye stops
      // reading — the point where a real deny stops standing out.
      rowClassName={(row) => (row.editable ? undefined : "opacity-70")}
    />
  );
}
